import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GLabel } from '../../pdf/glabel';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/shapes';
import '../../pdf/draw/helpers';
import '../../pdf/draw/label';

/**
 * Hra (Board Game) - Img10 version
 *
 * Layout: 6 pages (3 pairs), each pair shows questions (A) and answers (B).
 * Snake-like path with START, questions, jump targets, and FINISH.
 *
 * Migrated from C# EduFrame.Img10.Generators.Hra
 */
export class HraDopredu implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 4;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    for (let i = 0; i < 6; i++) {
      pdf.addPage();
    }
    this.generateHelper(pdf, opts, 0, 4);
    this.generateHelper(pdf, opts, 2, 5);
    this.generateHelper(pdf, opts, 4, 6);
  }

  private generateHelper(pdf: PdfGen, opts: OptsLabel, offset: number, countX: number): void {
    const margin = PdfUnit.FromMillimeters(10);
    const rect = new PdfRect(
      margin,
      margin,
      PdfSize.A4.width - 2 * margin,
      PdfSize.A4.height - 2 * margin
    );

    const ss = rect.width / (countX * 2 - 1);
    const size = new PdfSize(ss, ss);
    const countY = Math.floor(rect.height / ss);

    const baseRect = new PdfRect(
      rect.left,
      rect.top + (rect.height - size.height * countY) / 2,
      size.width * (countX * 2 - 1),
      size.height * countY
    );

    const gm: { x: number; y: number }[] = [];
    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        gm.push({ x: x * 2, y: x % 2 === 0 ? y : countY - 1 - y });
      }
      if (x + 1 < countX) {
        gm.push({ x: x * 2 + 1, y: x % 2 === 0 ? countY - 1 : 0 });
      }
    }

    const game = this.generateGame(gm.length - 1, opts);

    const allTextsA: string[][] = [];
    const allTextsB: string[][] = [];
    const labelMapA = new Map<number, GLabel>();
    const labelMapB = new Map<number, GLabel>();

    for (let i = 1; i < game.length - 1; i++) {
      if (game[i].jumpItemIndex !== null) {
        const itemIndex = game[i].jumpItemIndex!;
        if (itemIndex < opts.list.length) {
          const leftText = opts.list[itemIndex]?.left.getText() ?? '';
          if (leftText) {
            allTextsA.push([leftText]);
          }
          labelMapA.set(i, opts.list[itemIndex].left);
        }
      } else if (game[i].labelItemIndex !== null) {
        const itemIndex = game[i].labelItemIndex!;
        if (itemIndex < opts.list.length) {
          const rightText = opts.list[itemIndex]?.right.getText() ?? '';
          if (rightText) {
            allTextsB.push([rightText]);
          }
          labelMapB.set(i, opts.list[itemIndex].right);
        }
      }
    }

    const fontA =
      allTextsA.length > 0
        ? pdf.getPage(0).adjustFontMulti(allTextsA, GUtil.baseFont, size)
        : GUtil.baseFont;
    const fontB =
      allTextsB.length > 0
        ? pdf.getPage(0).adjustFontMulti(allTextsB, GUtil.baseFont, size)
        : GUtil.baseFont;

    for (let pp = 0; pp < 2; pp++) {
      const page = pdf.getPage(offset + pp);
      const hasPlus = pp === 0;

      for (let i = gm.length - 1; i >= 0; i--) {
        const pos = gm[i];
        let r = new PdfRect(
          baseRect.left + size.width * pos.x,
          baseRect.top + size.height * pos.y,
          size.width,
          size.height
        );

        if (i === 0) {
          r = r.inflate(5);
          const boldPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 6);
          page.drawRectangle(boldPen, null, r);
          page.drawRectangle(null, PdfBrush.White, r);
          page.drawString('START', GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.Center);
        } else if (i === gm.length - 1) {
          this.drawFinishSquare(page, r);
        } else if (game[i].jumpItemIndex !== null) {
          const label = labelMapA.get(i);
          if (label) {
            page.drawLabel(label, { rect: r, font: fontA });
          }

          if (hasPlus) {
            const jumpTo = game[i].jumpTo!;
            const plusStr = `+${jumpTo - i}`;
            const plusFont = new PdfFont(PdfFont.FONT_NAME, 12);
            const plusWidth = page.pdf
              .getFont(plusFont.font)
              .widthOfTextAtSize(plusStr, plusFont.size);
            const rr = new PdfRect(r.right - plusWidth, r.top, plusWidth, plusWidth);
            const redBrush = new PdfBrush(PdfColor.fromRgb(255, 0, 0, 0.8));
            page.drawRectangle(null, redBrush, rr);
            page.drawString(plusStr, plusFont, PdfBrush.White, null, rr, ETextAlign.TopRight);
          }
          page.drawRectangle(GUtil.basePen, null, r);
        } else if (game[i].labelItemIndex !== null) {
          const label = labelMapB.get(i);
          if (label) {
            page.drawLabel(label, { rect: r, font: fontB });
          }
          page.drawRectangle(GUtil.basePen, null, r);
        } else {
          const yellowBrush = new PdfBrush(PdfColor.fromRgb(255, 255, 230));
          page.drawRectangle(null, yellowBrush, r);
          page.drawString(`${i}`, GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.Center);
          page.drawRectangle(GUtil.basePen, null, r);
        }
      }
    }
  }

  private drawFinishSquare(page: any, r: PdfRect): void {
    const finish = 5;
    const sz1 = new PdfSize(r.width / finish, r.height / finish);
    const sz2 = new PdfSize(sz1.width / 2, sz1.height / 2);

    for (let x = 0; x < finish; x++) {
      for (let y = 0; y < finish; y++) {
        const r1 = new PdfRect(r.x + x * sz1.width, r.y + y * sz1.height, sz2.width, sz2.height);
        const r2 = new PdfRect(
          r.x + x * sz1.width + sz2.width,
          r.y + y * sz1.height + sz2.height,
          sz2.width,
          sz2.height
        );
        page.drawRectangle(null, PdfBrush.Black, r1);
        page.drawRectangle(null, PdfBrush.Black, r2);
      }
    }
    page.drawRectangle(GUtil.basePen, null, r);
  }

  private generateGame(count: number, opts: OptsLabel): GameSquare[] {
    while (true) {
      try {
        return this.generatePrivate(count, opts);
      } catch {
        // Retry on collision
      }
    }
  }

  private generatePrivate(count: number, opts: OptsLabel): GameSquare[] {
    const list: GameSquare[] = [];
    for (let i = 0; i < count; i++) {
      list.push(new GameSquare(i));
    }

    const itemCount = opts.list.length;
    const jumpPositions: number[] = [];

    const minJump = 5;
    const maxJump = count - 10;

    for (let i = minJump; i < maxJump; i++) {
      jumpPositions.push(i);
    }

    const {random} = opts.data;
    for (let i = jumpPositions.length - 1; i > 0; i--) {
      const j = random.next(0, i + 1);
      [jumpPositions[i], jumpPositions[j]] = [jumpPositions[j], jumpPositions[i]];
    }

    const selectedJumps = jumpPositions.slice(0, Math.min(itemCount - 1, jumpPositions.length));
    const extraJump = 2 + random.next(0, 5);
    selectedJumps.push(extraJump);

    for (let i = 0; i < selectedJumps.length; i++) {
      list[selectedJumps[i]].jumpItemIndex = i;
    }

    for (let i = 0; i < selectedJumps.length; i++) {
      const jumpIndex = selectedJumps[i];
      const targetIndex = this.findEmpty(
        jumpIndex + 3,
        Math.min(jumpIndex + 10, count - 1),
        list,
        random
      );
      list[jumpIndex].jumpTo = targetIndex;
      list[targetIndex].labelItemIndex = i;
    }

    return list;
  }

  private findEmpty(min: number, max: number, list: GameSquare[], random: any): number {
    const start = min + random.next(0, max - min);
    let ii = start;

    while (true) {
      if (list[ii].isEmpty()) {
        return ii;
      }
      ii++;
      if (ii >= max) {
        ii = min;
      }
      if (ii === start) {
        throw new Error('No empty slot found');
      }
    }
  }
}

class GameSquare {
  index: number;
  jumpItemIndex: number | null = null;
  jumpTo: number | null = null;
  labelItemIndex: number | null = null;

  constructor(index: number) {
    this.index = index;
  }

  isEmpty(): boolean {
    return this.jumpItemIndex === null && this.labelItemIndex === null;
  }
}
